import React from 'react';
import { useTranslation } from 'react-i18next';

import hasTextVariantsForCondition from 'editor/src/store/design/selector/hasTextVariantsForCondition';
import { Condition, ConditionGroup } from 'editor/src/store/design/types';
import { useSelector } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';

import useTr from 'editor/src/util/useTr';

import { DropDownContext } from 'editor/src/component/DropDown/DropDownContainer';

import DisplaySection from './DisplaySection';
import ElementsSection from './ElementsSection';
import OptionsSection from './OptionsSection';
import Sections from './Sections';
import Section from './Sections/Section';
import VaryingTextsSection from './VaryingTextsSection';

import styles from './index.module.scss';

interface Props {
  condition: Condition;
  conditionGroup: ConditionGroup;
  spreadIndex: number;
}

const CONTAINER_ID = 'condition-container';

function ConditionSettings({ condition, conditionGroup, spreadIndex }: Props) {
  const { t } = useTranslation();
  const tr = useTr();

  // Same gate as the mask/hint controls. A condition that already drives
  // per-option text keeps its section either way, or an imported template would
  // have the data in effect with no way to see or undo it.
  //
  // The gate has to travel INTO the section as well as deciding whether to show
  // it: the capture path in updateMediaElementOperation is gated on this same
  // setting, so a fully interactive section on an ungated host would let the
  // author switch a label on, retype it on the canvas, and have every edit
  // dropped — then overwritten from the stored variant on the next option
  // switch. Read-only keeps the "see and undo" purpose without offering
  // authoring that cannot work.
  const variantsEnabled = useSelector((state) => getHostSetting(state, 'enableTextConditionVariants'));
  const conditionHasVariants = useSelector((state) => hasTextVariantsForCondition(state, condition.id));
  const showVaryingTexts = variantsEnabled || conditionHasVariants;

  // Built as an array rather than with an inline `&&`: Sections lays its
  // children out by index and reads `child.props.title`, so a `false` in the
  // list would both offset the stack and throw.
  const sections = [
    <Section key="display" title={t('editor-condition-display-view')}>
      <DisplaySection spreadIndex={spreadIndex} condition={condition} />
    </Section>,
    <Section key="options" title={t('editor-condition-options')}>
      <OptionsSection spreadIndex={spreadIndex} condition={condition} />
    </Section>,
    <Section key="elements" title={t('editor-condition-associated-elements')}>
      <ElementsSection spreadIndex={spreadIndex} conditionGroup={conditionGroup} condition={condition} />
    </Section>,
  ];
  if (showVaryingTexts) {
    sections.push(
      <Section key="varyingTexts" title={tr('editor-condition-follow-texts', 'Text that follows this choice')}>
        <VaryingTextsSection spreadIndex={spreadIndex} condition={condition} authoringEnabled={variantsEnabled} />
      </Section>,
    );
  }

  return (
    <DropDownContext.Provider value={CONTAINER_ID}>
      <Sections className={styles.ConditionSettings}>{sections}</Sections>
      <div id={CONTAINER_ID} className={styles.dropdownContainer} />
    </DropDownContext.Provider>
  );
}

export default React.memo(ConditionSettings);
